import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";

const links = [
  { to: "/", label: "Kezdőlap" },
  { to: "/termekek", label: "Kollekció" },
  { to: "/kerdoiv", label: "Hajtípus" },
  { to: "/filozofia", label: "Filozófia" },
  { to: "/blog", label: "Blog" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { totalQty, openDrawer } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Pages with a dark cover hero — nav must be light over them when not scrolled.
  const DARK_COVER_PAGES = ["/", "/termekek", "/filozofia", "/b2b", "/kapcsolat"];
  const hasDarkCover = DARK_COVER_PAGES.includes(location.pathname);
  const overHero = !scrolled && hasDarkCover;

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border,padding] duration-500 ${
        scrolled ? "backdrop-blur-md border-b border-[color:var(--he-line)] py-0" : "border-b border-transparent py-2"
      }`}
      style={{
        color: overHero ? "#FFFFFF" : "var(--he-ink)",
        backgroundColor: scrolled ? "rgba(250, 249, 246, 0.94)" : "transparent",
      }}
    >
      <div className="he-container flex items-center justify-between h-20 md:h-24">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-3 leading-none"
          style={{ color: "inherit" }}
        >
          <img
            src="/logo.png"
            alt="Hair Elixir logó"
            className="h-10 md:h-12 w-auto object-contain"
          />
          <span className="font-serif-display text-[1.4rem] md:text-[1.7rem] tracking-wide flex items-baseline gap-2">
            <span>Hair</span>
            <span className="italic" style={{ color: "var(--he-gold)" }}>Elixir</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-11" aria-label="Főmenü">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              style={{ color: "inherit", opacity: overHero ? 0.92 : 0.78 }}
              className="he-link overline-xs"
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-7">
          <Link
            to="/kapcsolat"
            data-testid="nav-cta-inquiry"
            className="inline-flex items-center gap-3 group"
            style={{ color: "inherit" }}
          >
            <span className="overline-xs" style={{ opacity: overHero ? 0.92 : 0.78 }}>
              Érdeklődés
            </span>
            <span
              className="w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-500 group-hover:bg-[color:var(--he-gold)] group-hover:border-[color:var(--he-gold)] group-hover:text-white"
              style={{ borderColor: "currentColor" }}
            >
              <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>

          <button
            type="button"
            onClick={openDrawer}
            data-testid="nav-cart"
            aria-label="Kosár megnyitása"
            className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-500 hover:bg-[color:var(--he-gold)] hover:border-[color:var(--he-gold)] hover:text-white"
            style={{ color: "inherit", borderColor: "currentColor" }}
          >
            <ShoppingBag size={14} strokeWidth={1.7} />
            {totalQty > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--he-gold)] text-[10px] font-medium text-[color:var(--he-ink)] flex items-center justify-center leading-none"
                style={{ letterSpacing: 0 }}
              >
                {totalQty}
              </span>
            )}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <button
            type="button"
            onClick={openDrawer}
            data-testid="nav-cart-mobile"
            aria-label="Kosár"
            className="relative inline-flex items-center justify-center w-9 h-9"
            style={{ color: "inherit" }}
          >
            <ShoppingBag size={18} strokeWidth={1.7} />
            {totalQty > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[color:var(--he-gold)] text-[10px] font-medium text-[color:var(--he-ink)] flex items-center justify-center leading-none">
                {totalQty}
              </span>
            )}
          </button>
          <button
            aria-label="Menü"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            style={{ color: "inherit" }}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 bg-[color:var(--he-bg)] border-t border-[color:var(--he-line)] ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
        style={{ color: "var(--he-ink)" }}
      >
        <div className="he-container py-8 flex flex-col gap-5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mnav-${l.to.replace("/", "") || "home"}`}
              className="font-serif-display text-3xl"
            >
              {l.label}
            </NavLink>
          ))}
          <div className="he-divider mt-3" />
          <Link to="/kapcsolat" className="overline-xs" data-testid="mnav-inquiry">
            Kapcsolat →
          </Link>
        </div>
      </div>
    </header>
  );
}
